import axios from 'axios';

// ─── Auth mode toggle ──────────────────────────────────────────────────────────
//
// REACT_APP_AUTH_MODE=simple  → use localStorage token from /auth/login (default)
// REACT_APP_AUTH_MODE=okta    → use Cognito JWT from aws-amplify
//
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'simple';

export const TOKEN_KEY = 'itgi_auth_token';

// ─── Axios Instance ────────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Request Interceptor ────────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  async (config) => {
    try {
      if (AUTH_MODE === 'okta') {
        // Break-glass admin session takes priority — HMAC token in localStorage
        const hmacToken = localStorage.getItem(TOKEN_KEY);
        if (hmacToken) {
          config.headers.Authorization = `Bearer ${hmacToken}`;
        } else {
          // SSO user — use Cognito ID token.
          // The CognitoUserPoolsAuthorizer in API Gateway validates the `aud`
          // claim, which is only present on ID tokens (not access tokens).
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          if (token) config.headers.Authorization = `Bearer ${token}`;
        }
      } else {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      console.warn('Could not attach auth token:', err);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Simple-auth helpers ────────────────────────────────────────────────────────

/** POST /auth/login — returns token string on success, throws on failure */
export const login = async (username, password) => {
  const res = await axios.post(
    `${process.env.REACT_APP_API_URL}/auth/login`,
    { username, password },
    { headers: { 'Content-Type': 'application/json' } }
  );
  const token = res.data?.token;
  if (!token) throw new Error('No token in response');
  localStorage.setItem(TOKEN_KEY, token);
  return token;
};

export const logout = () => {
  localStorage.removeItem(TOKEN_KEY);
};

// ─── Infrastructure / Resources ───────────────────────────────────────────────

/**
 * Fetch all AWS resources, optionally filtered.
 *
 * Expected response shape:
 * {
 *   resources: [{ resource_id, name, resource_type, account_id, region,
 *                 status, tags, details, last_updated }],
 *   summary:   { total, ec2, rds, sg, last_refresh,
 *                by_account: [{ account_id, ec2_count, rds_count,
 *                               sg_count, vpc_count }] }
 * }
 *
 * @param {Object} filters - Optional server-side filters:
 *   { accounts: string[], regions: string[], types: string[],
 *     tagKey: string, tagValue: string, search: string }
 */
export const getResources = (filters = {}) =>
  apiClient.get('/resources', { params: filters }).then((res) => res.data);

/**
 * Trigger a background re-scan of all AWS accounts/regions.
 * The API returns immediately; the scan runs asynchronously.
 *
 * Expected response: { message: string, job_id: string }
 */
export const refreshResources = () =>
  apiClient.post('/resources/refresh').then((res) => res.data);

// ─── Security Groups ──────────────────────────────────────────────────────────

/**
 * Fetch all security groups across all accounts and regions.
 *
 * Expected response:
 * {
 *   security_groups: [{ group_id, group_name, account_id, region,
 *                       vpc_id, description }]
 * }
 */
export const getSecurityGroups = () =>
  apiClient.get('/security-groups').then((res) => res.data);

/**
 * Fetch all inbound rules for a specific security group.
 *
 * Expected response:
 * {
 *   rules: [{ rule_id, cidr_ip, ip_protocol, from_port, to_port,
 *             description }]
 * }
 *
 * @param {string} sgId - Security Group ID, e.g. "sg-0abc1234567890def"
 */
export const getSGRules = (sgId) =>
  apiClient.get(`/security-groups/${sgId}/rules`).then((res) => res.data);

/**
 * Search all ~1,000 SG rules for one or more IP addresses / CIDRs.
 *
 * Expected response:
 * {
 *   results: {
 *     "203.0.113.5": {
 *       found: true,
 *       matches: [{ sg_id, sg_name, cidr_ip, ip_protocol, from_port,
 *                   to_port, description, account_id, region, rule_id }]
 *     },
 *     "198.51.100.1": { found: false, matches: [] }
 *   }
 * }
 *
 * @param {string[]} ips - Array of IPs or CIDRs to search
 */
export const searchIPs = (ips) =>
  apiClient.post('/security-groups/search-ips', { ips }).then((res) => res.data);

/**
 * Add a new inbound rule to a security group.
 *
 * @param {string} sgId
 * @param {Object} rule - { cidr, protocol, from_port, to_port, description }
 */
export const addSGRule = (sgId, rule) =>
  apiClient.post(`/security-groups/${sgId}/rules`, rule).then((res) => res.data);

/**
 * Update only the description of an existing rule.
 * (AWS supports updating rule descriptions without deleting/re-creating the rule.)
 *
 * @param {string} sgId
 * @param {string} ruleId  - The SecurityGroupRuleId from AWS
 * @param {string} description
 */
export const updateSGRuleDescription = (sgId, ruleId, description) =>
  apiClient
    .put(`/security-groups/${sgId}/rules/${encodeURIComponent(ruleId)}`, { description })
    .then((res) => res.data);

/**
 * Delete an inbound rule from a security group.
 *
 * @param {string} sgId
 * @param {string} ruleId
 */
export const deleteSGRule = (sgId, ruleId) =>
  apiClient
    .delete(`/security-groups/${sgId}/rules/${encodeURIComponent(ruleId)}`)
    .then((res) => res.data);

// ─── Feature Requests ─────────────────────────────────────────────────────────

export const getFeatureRequests = () =>
  apiClient.get('/feature-requests').then((res) => res.data);

export const createFeatureRequest = (title, description = '') =>
  apiClient.post('/feature-requests', { title, description }).then((res) => res.data);

export const updateFeatureRequestStatus = (requestId, status) =>
  apiClient.patch(`/feature-requests/${requestId}`, { status }).then((res) => res.data);

// ─── Audit Log ───────────────────────────────────────────────────────────────

/**
 * Decode the stored simple-auth token and return the username, or null.
 * Token format: base64url({"usr":"...","exp":...}).<hex_hmac>
 */
export const getTokenUser = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  try {
    const payloadB64 = token.split('.')[0];
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
    return payload.usr || null;
  } catch {
    return null;
  }
};

/**
 * Fetch audit log events (admin only).
 *
 * Expected response:
 * { events: [{ timestamp, user, action, resource, details }], total: number }
 *
 * @param {number} [limit] - max events to return (default 500, max 1000)
 */
export const getAuditLog = (limit) =>
  apiClient.get('/audit', { params: limit ? { limit } : {} }).then((res) => res.data);

// ─── Backups ──────────────────────────────────────────────────────────────────

/**
 * Fetch the list of available backup dates.
 *
 * Expected response:
 * {
 *   backups: [{ date, sg_count, status, created_at }]
 * }
 */
export const getBackups = () =>
  apiClient.get('/backups').then((res) => res.data);

/**
 * Fetch per-SG backup details for a specific date.
 *
 * Expected response:
 * {
 *   security_groups: [{ group_id, group_name, account_id, rule_count,
 *                       download_url }]
 * }
 *
 * @param {string} date - ISO date string, e.g. "2024-01-15"
 */
export const getBackupByDate = (date) =>
  apiClient.get(`/backups/${date}`).then((res) => res.data);

// ─── Health Events ────────────────────────────────────────────────────────────

export const getHealthEvents = (filters = {}) =>
  apiClient.get('/health-events', { params: filters }).then((res) => res.data);

export const refreshHealthEvents = () =>
  apiClient.post('/health-events/refresh').then((res) => res.data);

export const ignoreHealthEvent = (eventArn, action) =>
  apiClient.post('/health-events/ignore', { event_arn: eventArn, action }).then((res) => res.data);

// ─── Mattermost Support Packet Analysis ───────────────────────────────────────

export const getMmPresign = (filename) =>
  apiClient.get('/mm-analyze/presign', { params: { filename } }).then((r) => r.data);

/**
 * Upload all chunks of a file to S3 using the pre-signed part URLs.
 * Pass an AbortSignal to cancel mid-upload.
 * Returns the parts array [{PartNumber, ETag}] needed to complete the upload.
 */
export const uploadMmChunks = async (presignData, file, onProgress, signal) => {
  const { part_urls, part_size } = presignData;
  const chunkSize   = part_size || 10 * 1024 * 1024;
  const numParts    = Math.ceil(file.size / chunkSize);
  const parts       = [];
  let uploadedBytes = 0;

  for (let i = 0; i < numParts; i++) {
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');

    const start = i * chunkSize;
    const end   = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);

    const etag = await _putPart(part_urls[i], chunk, (loaded) => {
      if (onProgress) onProgress(Math.round(((uploadedBytes + loaded) / file.size) * 100));
    }, signal);

    uploadedBytes += (end - start);
    parts.push({ PartNumber: i + 1, ETag: etag });
  }

  return parts;
};

export const processMmUpload = ({ report_id, zip_key, upload_id }, parts, opts = {}) =>
  apiClient
    .post('/mm-analyze/process', { report_id, zip_key, upload_id, parts }, { signal: opts.signal })
    .then((r) => r.data);

export const abortMmUpload = ({ upload_id, zip_key }) =>
  apiClient.post('/mm-analyze/abort', { upload_id, zip_key }).then((r) => r.data);

export const getMmStatus = (report_id) =>
  apiClient.get(`/mm-analyze/status/${report_id}`).then((r) => r.data);

export const getMmReports = () =>
  apiClient.get('/mm-analyze/reports').then((res) => res.data);

export const deleteMmReport = (report_id) =>
  apiClient.delete(`/mm-analyze/reports/${report_id}`).then((r) => r.data);

export const renameMmReport = (report_id, friendly_name) =>
  apiClient.patch(`/mm-analyze/reports/${report_id}`, { friendly_name }).then((r) => r.data);

function _putPart(url, chunk, onProgress, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
        reject(new DOMException('Cancelled', 'AbortError'));
      });
    }

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    });

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag') || '');
      } else {
        reject(new Error(`Part upload failed — HTTP ${xhr.status}: ${xhr.responseText || 'no body'}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Part upload network error — check browser DevTools Console for CORS details'));
    };

    xhr.onabort = () => reject(new DOMException('Cancelled', 'AbortError'));

    xhr.send(chunk);
  });
}

export default apiClient;
